import { useEffect, useRef, useState } from 'react';
import { cellKey, parseCellKey } from '../dbGridModel';
import { shouldGridHandleKey } from './dbGridKeyGuard';
import { gridAxes, moveCursor, rectKeys, isPrintableKey } from './dbGridNav';

// Rows that fit on screen, for PageUp / PageDown (jsdom has no layout: 20).
function pageSize() {
  const wrap = document.querySelector('.dbm-grid-wrap');
  const row = wrap?.querySelector('tbody tr');
  return wrap && row?.offsetHeight ? Math.max(1, Math.floor(wrap.clientHeight / row.offsetHeight) - 1) : 20;
}

/**
 * Keyboard on the DB grid, the way Excel / DBeaver / DataGrip do it
 * (owner, 30.09.2026):
 *   Arrow           move the selected cell (it used to scroll the grid)
 *   Shift+Arrow     extend the selection from the anchor
 *   Ctrl+Arrow      jump to the edge; Home/End, Ctrl+Home/End; PageUp/Down
 *   a character     start editing the selected cell with that character
 *   Enter / F2      open the cell (the same as a double-click)
 *   Backspace       clear the selected cells (Delete keeps marking rows)
 *
 * Only while a CELL selection exists, nothing is being edited, no modal is
 * open, and the key does not belong to a text field (WHERE box, find bar,
 * SQL editor). A window listener on purpose: the virtualizer unmounts rows and
 * a committed editor unmounts its input, and both drop focus to <body>.
 *
 * Returns `cursorKey` — the cell the keyboard last moved to — for the grids to
 * scroll into view (null until the keyboard moves).
 */
export default function useDbGridKeyboardNav(opts) {
  const latest = useRef(opts);
  latest.current = opts;
  const cursorRef = useRef(null); // last keyboard position, as a cell key
  const [cursorKey, setCursorKey] = useState(null);

  useEffect(() => {
    const isTextTarget = (t) => {
      const tag = (t?.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || !!t?.isContentEditable;
    };
    const onKey = (e) => {
      const o = latest.current;
      if (!o.enabled || o.editing || !shouldGridHandleKey(e) || isTextTarget(e.target)) return;
      // The grid or nothing in particular: a key typed with focus on a tab strip
      // or the sidebar is not the grid's.
      const t = e.target;
      if (t instanceof Element && t !== document.body && t !== document.documentElement && !t.closest('.dbm-grid-wrap')) return;
      if (o.selectedCells.size === 0) return;
      const axes = gridAxes(o);
      const anchorKey = o.anchor ? cellKey(o.anchor.kind, o.anchor.id, o.anchor.col) : null;
      // The keyboard cursor, if it is still part of the selection; otherwise the
      // anchor (a click moved the selection since the last key).
      const fromKey = cursorRef.current && o.selectedCells.has(cursorRef.current) ? cursorRef.current : anchorKey;
      const from = fromKey ? axes.posOf(fromKey) : null;
      if (!from) return;

      const next = moveCursor(from, { key: e.key, ctrl: e.ctrlKey || e.metaKey }, { ...axes, pageSize: pageSize() });
      if (next) {
        e.preventDefault();
        const key = axes.keyAt(next.v, next.h);
        cursorRef.current = key;
        setCursorKey(key);
        if (e.shiftKey) {
          const a = anchorKey ? axes.posOf(anchorKey) : null;
          o.setSelectedCells(rectKeys(axes, a || from, next));
        } else {
          o.setCellAnchor(axes.cellAt(next.v, next.h));
          o.setSelectedCells(new Set([key]));
        }
        return;
      }

      const cur = parseCellKey(fromKey);
      if (e.key === 'Enter' || e.key === 'F2') {
        if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey || cur.kind !== 'r') return;
        e.preventDefault();
        o.openCell(cur.id, cur.col);
        return;
      }
      if (e.key === 'Backspace' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (!o.writable) return;
        e.preventDefault();
        e.stopImmediatePropagation(); // not also "mark the row" (Delete does that)
        // The same staged NULL as Ctrl+X, without the copy; pending rows keep theirs.
        const writes = [...o.selectedCells].map(parseCellKey).filter((c) => c.kind === 'r');
        o.applyCellWrites(writes.map((c) => ({ ...c, value: null })));
        return;
      }
      if (isPrintableKey(e)) {
        if (!o.writable) return;
        // A new (pending) row's cell is an input of its own: the key goes there.
        const typed = cur.kind === 'r' ? o.typeIntoCell(cur.id, cur.col, e.key) : o.typeIntoPendingCell?.(cur.id, cur.col, e.key);
        if (typed) e.preventDefault();
      }
    };
    // Capture: the grid's own bubble handler treats Backspace as "mark rows".
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  return cursorKey;
}
